/**
 * CardItemSync component.
 * This component represents an individual card item for the Synchronized Knowledge .
 */

import { Box, Text, Flex, Image } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import CategoryBadge from '../Badge/CategoryBadge';
import { GetAuthData } from '../../../../misc/GetAuthData';
import { parseISO, isAfter } from 'date-fns';
import { PiStarFill, PiUsers } from 'react-icons/pi';

const LABEL_DIBUKA = 'Pendaftaran Dibuka';
const LABEL_BERLANGSUNG = 'Klinik Sedang Berlangsung';
const LABEL_SELESAI = 'Klinik Sudah Selesai';

const parseJadwal = (value?: string): Date | null =>
  value ? parseISO(value.replace(' ', 'T')) : null;

/**
 * Tentukan label status klinik dari jadwal mulai dan selesai.
 * Jadwal mulai kosong atau belum tercapai -> pendaftaran dibuka.
 * Jadwal selesai kosong tidak dianggap selesai.
 */
const getKlinikStatusLabel = (mulai?: string, selesai?: string): string => {
  const now = new Date();
  const startDate = parseJadwal(mulai);
  const endDate = parseJadwal(selesai);

  if (!startDate || !isAfter(now, startDate)) return LABEL_DIBUKA;
  if (endDate && isAfter(now, endDate)) return LABEL_SELESAI;
  return LABEL_BERLANGSUNG;
};

const CardItemSync = ({ item, spacing = true, accessFor }) => {
  const useAuth = GetAuthData();
  const history = useHistory();

  const urlDetail =
    accessFor === 'kursus-video'
      ? '/detail/kursus-video'
      : '/detail/pengetahuan';

  const classItem =
    accessFor === 'kursus-video' ? 'video-course-item' : 'pengetahuan-item';

  const statusLabel = getKlinikStatusLabel(
    item?.jadwal_mulai,
    item?.jadwal_selesai
  );
  const isFinished = statusLabel === LABEL_SELESAI;

  const handleNavigate = () => {
    if (useAuth !== null) {
      window.location.href = `${urlDetail}/${item.id}`;
    } else {
      history.push('/login');
      window.scrollTo(0, 0);
    }
  };

  return (
    <Box
      h="auto"
      borderRadius="lg"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.200"
      bg="white"
      mr={{ base: 0, md: spacing ? 8 : 0 }}
      _hover={{ shadow: 'lg', cursor: 'pointer' }}
      transition="all"
      transitionDuration="500ms"
      p={4}
      onClick={handleNavigate}
    >
      {/* Thumbnail */}
      <Box position="relative">
        <Image
          src={item.thumbnail}
          backgroundSize="cover"
          bgRepeat="no-repeat"
          style={{ aspectRatio: '2/1' }}
          rounded="lg"
          className={classItem}
          opacity={isFinished ? '0.5' : '1'}
        />
        <Box position="absolute" right={4} top={4}>
          <CategoryBadge category={statusLabel} />
        </Box>
      </Box>

      {/* Content */}
      <Box mt="4">
        <Flex direction="column" gap={2}>
          {/* Title */}
          <Text fontSize="lg" fontWeight="semibold">
            {item.title.length > 39
              ? item.title.substring(0, 39) + '...'
              : item.title}
          </Text>

          <Flex direction="row" alignItems="center" className="gap-2">
            {/* Rating */}
            <Flex alignItems="center" direction="row" className="gap-1">
              <Text fontSize="sm">{item.rata_rata_rating ?? 0}</Text>
              <PiStarFill size={13} className="text-yellow-500" />
            </Flex>
            <Text>|</Text>
            {/* Total Participant */}
            <Flex direction="row" alignItems="center" className="gap-1">
              <PiUsers size={13} />
              <Text>{item?.count_peserta} Peserta</Text>
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

export default CardItemSync;
